export const isNotificationRead = (notification) => {
    const readFlag =
        notification?.isRead ??
        notification?.read ??
        notification?.is_read ??
        notification?.seen ??
        notification?.isSeen;

    if (typeof readFlag === 'string') {
        return ['1', 'true', 'yes', 'read'].includes(readFlag.toLowerCase());
    }

    if (typeof readFlag === 'number') {
        return readFlag === 1;
    }

    if (typeof readFlag === 'boolean') {
        return readFlag;
    }

    return Boolean(notification?.readAt || notification?.read_at);
};

export const getNotificationId = (notification) =>
    notification?.id ?? notification?._id ?? notification?.notificationId;

export const getNotificationRedirectUrl = (notification, user) => {
    if (!notification) return null;

    const directUrl =
        notification?.redirectUrl ||
        notification?.url ||
        notification?.route ||
        notification?.actionUrl;
    const clickValue =
        notification?.click_value ||
        notification?.clickValue ||
        notification?.extra_data?.clickValue ||
        notification?.extra_data?.click_value;

    const moduleType = String(
        notification?.module_type ||
        notification?.moduleType ||
        notification?.extra_data?.moduleType ||
        notification?.extra_data?.module_type ||
        notification?.extra_data?.entityType ||
        ''
    ).toLowerCase().trim();

    const keyType = String(
        notification?.notificationkeyType ||
        notification?.extra_data?.notificationkeyType ||
        notification?.extra_data?.keyType ||
        ''
    ).toUpperCase().trim();

    const title = String(notification?.title || '').toLowerCase();
    const description = String(
        notification?.description ||
        notification?.message ||
        notification?.body ||
        ''
    ).toLowerCase();

    const roleName = String(
        user?.roleName || (typeof user?.role === 'string' ? user.role : user?.role?.name || user?.role?.roleName) || ''
    ).trim().toLowerCase();
    const isPodcaster = roleName === 'podcaster' || Number(user?.roleId || user?.role?.id) === 3;
    const notificationTarget = [clickValue, directUrl].filter(Boolean).join(' ').toLowerCase();
    const clickValueSlug = String(clickValue || '').toLowerCase().trim();
    const isTrackApprovalRequest =
        keyType === 'CONTENT_APPROVAL' &&
        ['track-request-list', 'tracks-request-list', 'track_request_list', 'tracks_request_list'].includes(clickValueSlug);
    const albumSlugMatch = clickValueSlug.match(/^\/?albums\/([^/?#]+)\/?$/);
    const isAlbumOrTrackNotification = ['album', 'track', 'episode'].some((term) =>
        moduleType.includes(term) || notificationTarget.includes(term) || title.includes(term) || description.includes(term)
    );

    // Approved podcaster content links carry the album slug (for example,
    // "albums/test-album") and should open that album's details page.
    if (albumSlugMatch) {
        return `/albums/${encodeURIComponent(albumSlugMatch[1])}`;
    }

    // Podcasters manage their album from the Albums page, not the admin approval queue.
    if (isPodcaster && isAlbumOrTrackNotification && !isTrackApprovalRequest) {
        return '/albums';
    }

    // 1. Direct explicit redirect URL if already provided
    if (directUrl && typeof directUrl === 'string' && directUrl.trim() !== '') {
        return directUrl;
    }

    // 3. Match clickValue
    if (clickValue) {
        const val = String(clickValue).toLowerCase().trim();

        // Album approval routes
        if (
            val === 'album-request-list' ||
            val === 'album_request_list' ||
            val === 'album-publish-requests' ||
            val === 'album-publish-request' ||
            val === 'album-requests' ||
            val === 'albums-request-list' ||
            val === 'albums/publish-requests'
        ) {
            return '/albums/publish-requests';
        }

        // Track approval routes
        if (
            val === 'track-request-list' ||
            val === 'tracks-request-list' ||
            val === 'track_request_list' ||
            val === 'tracks_request_list' ||
            val === 'track-publish-requests' ||
            val === 'track-publish-request' ||
            val === 'tracks-publish-requests' ||
            val === 'albums/tracks/publish-requests'
        ) {
            return '/albums/tracks/publish-requests';
        }

        // Customer list
        if (val === 'customer-list' || val === 'customers') {
            return '/customers';
        }

        // If it starts with a leading slash or valid path
        if (val.startsWith('/')) {
            return clickValue;
        }
    }

    // 4. Match by CONTENT_APPROVAL or keywords in title / description
    const isApproval =
        keyType === 'CONTENT_APPROVAL' ||
        title.includes('pending approval') ||
        title.includes('approval') ||
        description.includes('approval') ||
        description.includes('waiting for your approval');

    if (isApproval) {
        if (moduleType === 'album' || description.includes('album') || title.includes('album')) {
            return '/albums/publish-requests';
        }
        if (moduleType === 'track' || moduleType === 'tracks' || description.includes('track') || title.includes('track')) {
            return '/albums/tracks/publish-requests';
        }
    }

    // 5. Fallback based on moduleType
    if (moduleType === 'album') {
        return '/albums/publish-requests';
    }
    if (moduleType === 'track' || moduleType === 'tracks') {
        return '/albums/tracks/publish-requests';
    }

    // 6. Registration notifications (client / customer registered)
    if (
        title.includes('client inscrit') ||
        description.includes('client') ||
        title.includes('customer') ||
        description.includes('customer')
    ) {
        return '/customers';
    }

    return null;
};
